/**
 * Drag-to-modify helpers used by `CalendarView/index.tsx`'s onEventDrop /
 * onEventResize handlers. Kept in their own module (rather than colocated
 * with the component) so unit tests can exercise the pure payload-shaping
 * logic without pulling in the rest of the calendar's context graph.
 *
 * Snap to 15-min increments matches the existing `step=30, timeslots=2`
 * Calendar config. `MIN_DURATION_MINS` is a client-side floor for the
 * bottom-edge resize; the backend doesn't reject smaller values but a
 * sub-15-min sliver is almost always an unintended drag overshoot.
 */

export const SNAP_INCREMENT_MINS = 15;
export const MIN_DURATION_MINS = 15;

export const snapToIncrement = (date: Date, incrementMins: number): Date => {
  const ms = incrementMins * 60 * 1000;
  return new Date(Math.round(date.getTime() / ms) * ms);
};

export interface ResizeResolution {
  snappedStart: Date;
  baseDurationMins: number;
  finalEnd: Date;
  startChanged: boolean;
}

/**
 * Resolve the snapped (start, duration_mins) pair for a resize event,
 * applying every rule the manual UX promises:
 *  * top-edge over-drag past the bottom is clamped to (end − 15 min)
 *    rather than pushing the bottom edge below the original;
 *  * bottom-edge resize keeps the start unsnapped (the user didn't grab
 *    that edge) while the duration is rounded to 15-min;
 *  * the final `baseDurationMins` is always a 15-min multiple ≥ floor.
 *
 * Returns the resolved values so the network payload + the optimistic
 * setEvents call use the same numbers.
 */
export interface RescheduleSmsInput {
  /** TimeBlocks have no client and never text. */
  isTimeBlock: boolean;
  /** Booked appointment carries a line_item_id (required to write). */
  hasLineItem: boolean;
  /** Booked appointment carries a transaction_id (required to write). */
  hasTransaction: boolean;
  /** The commit actually shifts start_dt (false for a duration-only resize). */
  startChanged: boolean;
}

/**
 * Whether committing this drag/resize will text the client the new time.
 * The backend fires the reschedule SMS only when start_dt shifts on a booked
 * appointment, so the modal warns the operator only when ALL hold:
 *  * not a TimeBlock (no client);
 *  * both ids present (an appointment missing them can't be written at all,
 *    so no SMS is possible — don't promise one);
 *  * start_dt changes (a bottom-edge, duration-only resize leaves it put).
 * It's still a heuristic — opted-out / past / unconfirmed clients are decided
 * backend-side and can't be known here — but it never over-promises on a
 * change that provably can't send.
 */
export const rescheduleFiresClientSms = (input: RescheduleSmsInput): boolean =>
  !input.isTimeBlock &&
  input.hasLineItem &&
  input.hasTransaction &&
  input.startChanged;

export const resolveResize = (
  event: { start: Date; end: Date },
  start: Date,
  end: Date,
  snapMins: number = SNAP_INCREMENT_MINS,
  minDurationMins: number = MIN_DURATION_MINS,
): ResizeResolution => {
  const startChanged = event.start.getTime() !== start.getTime();
  const endChanged = event.end.getTime() !== end.getTime();

  let snappedStart = startChanged
    ? snapToIncrement(start, snapMins)
    : event.start;
  const snappedEnd = endChanged ? snapToIncrement(end, snapMins) : event.end;

  // Clamp top-edge over-drag at end − minDuration so the bottom edge
  // doesn't drift past its original position.
  const earliestAllowedStartMs =
    snappedEnd.getTime() - minDurationMins * 60 * 1000;
  if (startChanged && snappedStart.getTime() > earliestAllowedStartMs) {
    snappedStart = new Date(earliestAllowedStartMs);
  }

  const rawDurationMins =
    (snappedEnd.getTime() - snappedStart.getTime()) / 60000;
  const snappedDurationMins = Math.round(rawDurationMins / snapMins) * snapMins;
  const baseDurationMins = Math.max(minDurationMins, snappedDurationMins);
  const finalEnd = new Date(
    snappedStart.getTime() + baseDurationMins * 60 * 1000,
  );

  return { snappedStart, baseDurationMins, finalEnd, startChanged };
};
